import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, forkJoin, map, of, switchMap } from "rxjs";
import { ApiService } from "../core/api.service";
import { CatalogBlind, Measurement, WindowEntity } from "../core/models";

@Component({
  selector: "app-window-edit-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  template: `
    <section class="dashboard">
      <div class="dashboard-main">
        <header class="dashboard-header">
          <div>
            <h1>{{ window()?.label || "Window" }}</h1>
          </div>
          <div class="header-actions">
            <button class="action-button" (click)="cancel()">Cancel</button>
            <button
              class="action-button primary"
              (click)="save()"
              [disabled]="!canSave() || saving()"
            >
              {{ saving() ? "Saving..." : "Save" }}
            </button>
          </div>
        </header>

        @if (!loading) {
          <div class="panel-card">
            <div class="panel-header">
              <h2>Window Information</h2>
            </div>
            <div class="panel-content">
              <div class="form-grid">
                <div class="form-field-group">
                  <label>Label</label>
                  <input class="form-input" [(ngModel)]="form.label" />
                </div>
                <div class="form-field-group">
                  <label>Width (inches)</label>
                  <input
                    class="form-input"
                    type="number"
                    [(ngModel)]="form.measurement.width"
                  />
                </div>
                <div class="form-field-group">
                  <label>Height (inches)</label>
                  <input
                    class="form-input"
                    type="number"
                    [(ngModel)]="form.measurement.height"
                  />
                </div>
                <div class="form-field-group">
                  <label>Control</label>
                  <select class="form-select" [(ngModel)]="form.controlType">
                    <option value="cordless">Cordless</option>
                    <option value="motorized">A-OK Matter motorized</option>
                  </select>
                </div>
                <div class="checkbox-field">
                  <input
                    type="checkbox"
                    [(ngModel)]="form.hasSolarPanel"
                    id="solar-check"
                  />
                  <label for="solar-check">Needs Solar</label>
                </div>
                <div class="form-field-group">
                  <label>Notes</label>
                  <input class="form-input" [(ngModel)]="form.notes" />
                </div>
                <div class="form-field-group">
                  <label>Style</label>
                  <input
                    class="form-input"
                    [(ngModel)]="form.style"
                    placeholder="Blind code"
                    list="style-suggestions"
                    autocomplete="off"
                  />
                  <datalist id="style-suggestions">
                    @for (b of styleOptions(); track b.id) {
                      <option [value]="b.semiBlackoutCode">{{ b.name }} — Semi blackout</option>
                      <option [value]="b.blackoutCode">{{ b.name }} — Blackout</option>
                    }
                  </datalist>
                </div>
                <div class="form-field-group">
                  <label>Measurement notes</label>
                  <input
                    class="form-input"
                    [(ngModel)]="form.measurement.notes"
                  />
                </div>
              </div>
            </div>
          </div>
        }

        @if (message(); as msg) {
          <div class="status-message">{{ msg }}</div>
        }
      </div>
    </section>
  `,
  styleUrls: ["./window-edit-page.component.scss"],
})
export class WindowEditPageComponent {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly customerId: string;
  readonly roomId: string;
  readonly windowId: string;
  readonly measurementId: string;

  readonly saving = signal(false);
  readonly message = signal("");

  /** Delegates to the window resource's built-in loading signal. */
  get loading() {
    return this.windowResource.isLoading();
  }

  form = {
    label: "",
    notes: "",
    style: "",
    controlType: "cordless" as "manual" | "cordless" | "motorized",
    hasSolarPanel: false,
    measurement: {
      width: 0,
      height: 0,
      notes: "",
    },
  };

  private readonly windowResource = rxResource({
    params: () => ({
      roomId: this.roomId,
      windowId: this.windowId,
      measurementId: this.measurementId,
    }),
    stream: ({ params }) =>
      this.api.getWindow(params.roomId, params.windowId).pipe(
        switchMap((window) => {
          const measurement$ = params.measurementId
            ? this.api
                .getMeasurement(params.windowId, params.measurementId)
                .pipe(catchError(() => of(null)))
            : of(null);
          return measurement$.pipe(
            map((measurement) => ({ window, measurement })),
          );
        }),
        map(({ window, measurement }) => {
          this._loadedMeasurement = measurement;
          this.populateForm(window, measurement);
          return window;
        }),
        catchError(() => {
          this.message.set("Failed to load window.");
          return of(null as WindowEntity | null);
        }),
      ),
  });
  readonly window = this.windowResource.value;

  /** Blind codes from the catalog, falling back to styles already in use. */
  private readonly styleOptionsResource = rxResource({
    params: () => this.customerId,
    stream: ({ params }) =>
      this.api.getCatalogBlinds().pipe(
        catchError(() => of([] as CatalogBlind[])),
        switchMap((blinds) =>
          blinds.length
            ? of(blinds)
            : this.api.getCustomerWindows(params).pipe(
                map((allWindows) => {
                  const styles = allWindows
                    .map((w) => w.style)
                    .filter((s): s is string => !!s && s.trim().length > 0);
                  return [...new Set(styles)].map(
                    (code) =>
                      ({
                        id: code,
                        name: code,
                        semiBlackoutCode: code,
                        blackoutCode: code,
                        createdAt: "",
                      }) as CatalogBlind,
                  );
                }),
                catchError(() => of([] as CatalogBlind[])),
              ),
        ),
      ),
  });
  readonly styleOptions = this.styleOptionsResource.value;

  constructor() {
    this.customerId = this.route.snapshot.paramMap.get("customerId") || "";
    this.roomId = this.route.snapshot.paramMap.get("roomId") || "";
    this.windowId = this.route.snapshot.paramMap.get("windowId") || "";
    this.measurementId =
      this.route.snapshot.queryParamMap.get("measurementId") || "";
  }

  populateForm(window: WindowEntity, measurement: Measurement | null) {
    this.form = {
      label: window.label ?? "",
      notes: window.notes ?? "",
      style: window.style ?? "",
      controlType: (window.controlType ??
        "cordless") as "manual" | "cordless" | "motorized",
      hasSolarPanel: window.hasSolarPanel ?? false,
      measurement: {
        width: measurement?.width ?? 0,
        height: measurement?.height ?? 0,
        notes: measurement?.notes ?? "",
      },
    };
  }

  canSave(): boolean {
    return (
      this.form.label.trim().length > 0 &&
      this.form.measurement.width > 0 &&
      this.form.measurement.height > 0
    );
  }

  cancel() {
    this.router.navigate(["/customers", this.customerId]);
  }

  save() {
    if (!this.canSave()) return;

    const width = Number(this.form.measurement.width);
    const height = Number(this.form.measurement.height);

    this.saving.set(true);

    const windowUpdate = {
      label: this.form.label.trim(),
      notes: this.form.notes.trim() || undefined,
      style: this.form.style.trim() || undefined,
      controlType: this.form.controlType,
      hasSolarPanel: this.form.hasSolarPanel,
    };

    const measurementPayload = {
      width,
      height,
      unit: "in" as const,
      notes: this.form.measurement.notes.trim() || undefined,
    };

    this.api
      .updateWindow(this.roomId, this.windowId, windowUpdate)
      .pipe(
        switchMap(() => {
          const current = this._loadedMeasurement;
          if (current) {
            return this.api.updateMeasurement(
              this.windowId,
              current.id,
              measurementPayload,
            );
          } else {
            return this.api.createMeasurement(
              this.windowId,
              measurementPayload,
            );
          }
        }),
      )
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.router.navigate(["/customers", this.customerId], {
            state: { message: "Window saved." },
          });
        },
        error: () => {
          this.saving.set(false);
          this.message.set("Failed to save window.");
        },
      });
  }

  private _loadedMeasurement: Measurement | null = null;
}
